import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { messagesApi } from '../api/endpoints.ts';
import { Alert, Button, LoadingBlock } from './ui.tsx';
import { WorkEmpty } from './work-page.tsx';
import { MessageRow } from './conversation-timeline.tsx';
import { MessageComposer, type ReplyContext } from './message-composer.tsx';
import { useIsPublicDemo } from './demo-limit-note.tsx';
import { InboxActionSuggestions } from './inbox-action-suggestions.tsx';
import { InboxQuoteActionSuggestions } from './inbox-quote-action-suggestions.tsx';
import { canAnswerInbound, replyChannelFor } from './call-details.tsx';

/**
 * One thread, read and answered in place.
 *
 * The inbox's half of the conversation. It works in CONVERSATIONS rather than
 * customers, which is the whole reason it exists separately from
 * `ConversationTimeline`: a text from a number nobody recognizes is a real
 * message waiting on a real answer and has no customer to hang off, so a
 * component keyed on a customer id could never show it.
 *
 * Opening a thread marks it read. That is a claim about a person having seen
 * it, and it is only true because this component does not render until the
 * messages are on screen — which is also why the customer page's own timeline
 * makes the same claim with a button instead: there, the thread is one section
 * among many and scrolling past it is not reading it.
 */

const THREAD_PAGE = 30;

export function ConversationThread({
  conversationId,
  customerId,
  canWrite,
}: {
  conversationId: string;
  /** Null for a thread nobody has attached to a customer yet. */
  customerId: string | null;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [shown, setShown] = useState(THREAD_PAGE);

  const thread = useQuery({
    queryKey: ['conversation-thread', conversationId, shown],
    queryFn: () => messagesApi.thread(conversationId, { pageSize: shown }),
    placeholderData: (previous) => previous,
  });

  const replyNonce = useRef(0);
  const [reply, setReply] = useState<ReplyContext | null>(null);
  if (reply) replyNonce.current = reply.nonce;

  // A different thread is a different conversation: the page grows back to its
  // first page and whatever was being answered stops applying.
  useEffect(() => {
    setShown(THREAD_PAGE);
    setReply(null);
  }, [conversationId]);

  const markRead = useMutation({
    mutationFn: () => messagesApi.markRead(conversationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  /**
   * Read on arrival, once.
   *
   * Guarded on the id rather than on the unread count, which changes under it
   * when the list refetches — without that, marking read invalidates the list,
   * the count comes back zero, and the effect fires again on every refresh.
   */
  const markedRef = useRef<string | null>(null);
  const messagesLoaded = Boolean(thread.data);
  // Never in the shared demo: one visitor opening a thread would clear its
  // unread badge for every visitor after them, and the API refuses the write.
  const isPublicDemo = useIsPublicDemo();
  useEffect(() => {
    if (isPublicDemo || !messagesLoaded || markedRef.current === conversationId) return;
    markedRef.current = conversationId;
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messagesLoaded, isPublicDemo]);

  /**
   * Newest first, the same way the customer page reads it — and reversed
   * rather than re-sorted for the same reason: the server has already broken
   * a shared timestamp by id, and a sort on `createdAt` would undo that.
   */
  const messages = [...(thread.data?.messages ?? [])].reverse();
  const hasOlder = messages.length >= shown;

  const lastInbound = messages.find((message) => canAnswerInbound(message, customerId));

  const suggested: ReplyContext | undefined = lastInbound
    ? {
        channel: replyChannelFor(lastInbound),
        subject: lastInbound.subject,
        replyToAddress: lastInbound.replyToAddress,
        nonce: 0,
      }
    : undefined;

  if (thread.isLoading) return <LoadingBlock label={t('common.loading')} />;

  return (
    <section className="messages-thread">
      {/* The box first, then what it is answering. The thread reads newest
          first, so the reply sits next to the message that prompted it rather
          than under however much history is loaded. */}
      {canWrite ? (
        <div className="messages-composer mb-4 border-b border-[color:var(--color-line)] pb-4">
          <MessageComposer
            {...(customerId ? { customerId } : {})}
            compactDelivery
            conversationId={conversationId}
            replyContext={reply ?? suggested}
            onSent={() => queryClient.invalidateQueries({ queryKey: ['conversation-thread', conversationId] })}
          />
        </div>
      ) : null}

      {thread.isError ? <Alert tone="error">{t('messagesInbox.threadError')}</Alert> : null}
      {thread.isError ? <Button onClick={() => void thread.refetch()}>{t('messagesInbox.retry')}</Button> : null}
      {!thread.isError && messages.length === 0 ? <WorkEmpty title={t('messagesInbox.threadEmpty')} /> : null}
      <div className="mb-3">
        <InboxActionSuggestions conversationId={conversationId} />
        <InboxQuoteActionSuggestions conversationId={conversationId} />
      </div>
      <ol className="mb-3 flex flex-col gap-3">
        {messages.map((message) => (
          <MessageRow
            key={message.id}
            localize
            message={message}
            customerId={customerId}
            onUseDraft={
              // An unknown caller's thread has no number to text until it is
              // attached to a customer.
              canWrite && customerId
                ? (text) =>
                    setReply({
                      channel: 'sms',
                      subject: null,
                      replyToAddress: null,
                      nonce: replyNonce.current + 1,
                      body: text,
                    })
                : undefined
            }
            onReply={
              canWrite && canAnswerInbound(message, customerId)
                ? () =>
                    setReply({
                      channel: replyChannelFor(message),
                      subject: message.subject,
                      // Whoever wrote it, not whoever the record names.
                      replyToAddress: message.replyToAddress,
                      nonce: replyNonce.current + 1,
                    })
                : undefined
            }
          />
        ))}
      </ol>

      {/* Below the list: what this loads is older than everything above it. */}
      {hasOlder ? (
        <div>
          <Button
            variant="secondary"
            busy={thread.isFetching}
            onClick={() => setShown((current) => current + THREAD_PAGE)}
          >
            {t('conversation.loadEarlier')}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
